import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Box,
    Typography,
    Tooltip,
    Slider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';

const IMAGE_EXT = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
const PDF_EXT = ['.pdf'];

const getFileType = (urlOrFileName) => {
    if (!urlOrFileName) return 'unknown';
    const str = typeof urlOrFileName === 'string' ? urlOrFileName : urlOrFileName?.name || '';
    const lower = str.split('?')[0].toLowerCase();
    if (IMAGE_EXT.some((ext) => lower.endsWith(ext))) return 'image';
    if (PDF_EXT.some((ext) => lower.endsWith(ext))) return 'pdf';
    return 'other';
};

const AssignmentFilePreviewModal = ({ open, onClose, url, fileName, mimeType }) => {
    const { t } = useTranslation();
    const [zoom, setZoom] = useState(100);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [displayUrl, setDisplayUrl] = useState(url);

    useEffect(() => {
        setDisplayUrl(url);
        setZoom(100);
    }, [url]);

    const fileType = useCallback(() => {
        if (mimeType) {
            if (mimeType.startsWith('image/')) return 'image';
            if (mimeType === 'application/pdf') return 'pdf';
        }
        return getFileType(fileName || url);
    }, [mimeType, fileName, url])();

    const handleZoomIn = () => setZoom((z) => Math.min(z + 25, 300));
    const handleZoomOut = () => setZoom((z) => Math.max(z - 25, 25));
    const handleZoomChange = (_, v) => setZoom(v);
    const toggleFullscreen = () => setIsFullscreen((f) => !f);

    const handleDownload = () => {
        if (!displayUrl) return;
        const a = document.createElement('a');
        a.href = displayUrl;
        a.download = fileName || 'document';
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    if (!open) return null;

    const zoomValue = zoom / 100;
    const contentSx = {
        width: '100%',
        height: isFullscreen ? 'calc(100vh - 120px)' : '70vh',
        minHeight: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'auto',
        backgroundColor: '#2d2d2d',
        borderRadius: 1,
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={isFullscreen ? false : 'xl'}
            fullWidth={!isFullscreen}
            fullScreen={isFullscreen}
            PaperProps={{
                sx: {
                    ...(isFullscreen && { maxWidth: '100%', maxHeight: '100%', m: 0, borderRadius: 0 }),
                },
            }}
            sx={{ '& .MuiDialog-paper': { minHeight: isFullscreen ? '100vh' : 500 } }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5,
                    px: 2,
                    borderBottom: '1px solid #e0e0e0',
                    backgroundColor: '#f5f5f5',
                }}
            >
                <Typography variant="h6" noWrap sx={{ flex: 1, mr: 2 }}>
                    {t('DocumentPreview')} {fileName ? `- ${fileName}` : ''}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={t('ZoomIn') || 'Zoom in'}>
                        <IconButton onClick={handleZoomIn} size="small">
                            <ZoomInIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('ZoomOut') || 'Zoom out'}>
                        <IconButton onClick={handleZoomOut} size="small">
                            <ZoomOutIcon />
                        </IconButton>
                    </Tooltip>
                    <Slider
                        value={zoom}
                        min={25}
                        max={300}
                        step={5}
                        onChange={handleZoomChange}
                        sx={{ width: 80 }}
                        size="small"
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v}%`}
                    />
                    <Tooltip title={isFullscreen ? (t('ExitFullscreen') || 'Exit fullscreen') : (t('Fullscreen') || 'Fullscreen')}>
                        <IconButton onClick={toggleFullscreen} size="small">
                            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('Download') || 'Download'}>
                        <IconButton onClick={handleDownload} size="small">
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={contentSx}>
                    {displayUrl && fileType === 'image' && (
                        <img
                            src={displayUrl}
                            alt={fileName || 'Preview'}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                transform: `scale(${zoomValue})`,
                                transformOrigin: 'center center',
                            }}
                        />
                    )}
                    {displayUrl && fileType === 'pdf' && (
                        <iframe
                            src={`${displayUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                            title={fileName || 'PDF Preview'}
                            style={{
                                width: `${100 * zoomValue}%`,
                                height: '100%',
                                minHeight: 400,
                                border: 'none',
                                borderRadius: 4,
                            }}
                        />
                    )}
                    {displayUrl && fileType === 'other' && (
                        <Box sx={{ textAlign: 'center', color: '#fff', p: 3 }}>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {t('PreviewNotAvailable') || 'Preview not available for this file type.'}
                            </Typography>
                            <IconButton
                                onClick={handleDownload}
                                sx={{ color: '#fff', border: '1px solid rgba(255,255,255,0.5)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                            >
                                <DownloadIcon sx={{ mr: 1 }} />
                                {t('Download') || 'Download'}
                            </IconButton>
                        </Box>
                    )}
                    {!displayUrl && (
                        <Typography color="textSecondary">{t('NoPreview') || 'No file to preview'}</Typography>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default AssignmentFilePreviewModal;
